import { FC, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { BonusClaimedColor, getSlotBadge } from 'utils/scarcity';

import { BonusSlots } from '../../bonuses';
import { BonusUnlockStatus } from '../../useBonusUnlocks';

// The colours certify's mega menu paints its counters with, so the badge reads identically on both sites.
const CLAIMED_COLORS: Record<BonusClaimedColor, string> = {
  green: '#22C55E',
  yellow: '#ED5A26',
  red: '#EF4444',
};
const QUIET_COLOR = '#9CA3AF';

type BonusBadgeProps = {
  /** No slots: a bonus without a live counter shows "Included". */
  slots?: BonusSlots;
  /** Set only once the student has unlocked the bonus — never by default. */
  status?: BonusUnlockStatus;
};

/** Live "X / N claimed" pill, re-read every minute like certify's. */
const SlotBadge: FC<{ slots: BonusSlots }> = ({
  slots: { maxUnits, delayHours, cooldownHours, finalLowUnitsThreshold },
}) => {
  const [badge, setBadge] = useState(() =>
    getSlotBadge({ maxUnits, delayHours, cooldownHours, finalLowUnitsThreshold }),
  );

  useEffect(() => {
    const update = () => setBadge(getSlotBadge({ maxUnits, delayHours, cooldownHours, finalLowUnitsThreshold }));
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [maxUnits, delayHours, cooldownHours, finalLowUnitsThreshold]);

  // Between cohorts the count goes quiet instead of visibly falling back.
  return <Pill $background={badge.quiet ? QUIET_COLOR : CLAIMED_COLORS[badge.color]}>{badge.text}</Pill>;
};

const BonusBadge: FC<BonusBadgeProps> = ({ slots, status }) => {
  if (status === 'unlocked') {
    return <Pill $variant="unlocked">Unlocked</Pill>;
  }

  if (slots) {
    return <SlotBadge slots={slots} />;
  }

  return <Pill $variant="included">Included</Pill>;
};

export default BonusBadge;

const Pill = styled.span<{ $variant?: 'included' | 'unlocked'; $background?: string }>`
  display: inline-block;
  flex-shrink: 0;
  white-space: nowrap;
  padding: 2px 8px;
  border-radius: 999px;

  font-size: 10px;
  font-weight: 900;
  line-height: 15px;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  color: #ffffff;
  background: ${({ $background }) => $background};

  ${({ $variant }) =>
    $variant === 'included' &&
    css`
      color: var(--bm-pill-ink);
      background: var(--bm-pill-bg);
    `}

  ${({ $variant }) =>
    $variant === 'unlocked' &&
    css`
      color: var(--bm-unlocked-ink);
      background: var(--bm-unlocked-bg);
    `}
`;
